import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Text,
  Badge,
  SimpleGrid,
  Select,
  Spinner,
  useDisclosure
} from '@chakra-ui/react'
import { Alert } from '../UI'
import CustomPaginate from '../UI/CustomPaginate'
import OptionsMenu from './OptionsMenu'
import { usePartners } from '../../hooks/Partners'
import WithoutResults from '../WithoutResults'
import ResignationModal from './ResignationModal'
import { partnerHierarchy } from '../../Utils/PartnerHierarchy'

const states = {
  ACTIVE: ['Activo', 'green.50', 'green.500'],
  'NOT-VALIDATED': ['No validado', 'yellow.100', 'yellow.800'],
  RESIGNED: ['En renuncia', 'red.50', 'red.700'],
  INACTIVE: ['Pendiente de dcto.', 'grey.50', 'gray.600'],
  ONHOLD: ['En espera', 'orange.50', 'orange.800'],
  DECEASED: ['Fallecido', 'red.50', 'red.700']
}
const PartnerState = ({ state }) => {
  const [label, bg, color] = states[state] || [
    state || 'Sin estado',
    'grey.50',
    'gray.600'
  ]
  return (
    <Badge
      bg={bg}
      color={color}
      borderRadius={6}
      px={2}
      py={1}
      fontSize="xs"
      textTransform="none"
      whiteSpace="normal"
    >
      {label}
    </Badge>
  )
}
const PartnerName = ({ partner }) => (
  <Link to={`/partners/profile/${partner._id}/Perfil de Socio`}>
    <Text fontWeight="semibold" color="green.500" overflowWrap="anywhere">
      {partner.name}
    </Text>
  </Link>
)
const Hierarchy = ({ partner }) => {
  const { hierarchicalGroup, hierarchicalGrade, cprGrade } =
    partnerHierarchy(partner)
  if (partner.officialType === 'CPR')
    return (
      <Box>
        <Text>Grado CPR: {cprGrade === '' ? 'Sin definir' : cprGrade}</Text>
        <Text fontSize="xs" color="gray.600">
          {cprGrade === ''
            ? 'Tramo pendiente'
            : cprGrade === 0
            ? 'Grado 0'
            : cprGrade <= 12
            ? 'Grados 1 a 12'
            : 'Grados 13 en adelante'}
        </Text>
      </Box>
    )
  return (
    <Box>
      <Text>{hierarchicalGrade || 'Grado sin definir'}</Text>
      <Text fontSize="xs" color="gray.600">
        Grupo: {hierarchicalGroup || 'Sin definir'}
        {partner.hierarchicalGroupSource === 'INFERRED' ? ' (estimado)' : ''}
      </Text>
    </Box>
  )
}

const TablePartners = ({ filtersPartners }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentId, setCurrentId] = useState(null)
  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(10)
  const {
    data: partners,
    isLoading,
    isFetching,
    isError
  } = usePartners(filtersPartners, page, limit)
  const rows = partners?.currentPages || []
  useEffect(() => {
    setPage(0)
  }, [filtersPartners])
  const options = (partner) => (
    <OptionsMenu
      partner={partner}
      onOpen={onOpen}
      setCurrentId={setCurrentId}
    />
  )

  return (
    <Box
      data-members-list
      minW={0}
      mt={3}
      position="relative"
      aria-busy={isFetching}
    >
      {isLoading && (
        <Flex py={6} gap={2} justify="center">
          <Spinner size="sm" />
          <Text>Cargando socios…</Text>
        </Flex>
      )}
      {!isLoading && isFetching && (
        <Spinner
          size="xs"
          position="absolute"
          right={0}
          top={-3}
          aria-label="Actualizando socios"
        />
      )}
      {isError && <Alert status="error" message="Error al cargar los datos" />}
      {!isLoading && !isError && !rows.length && (
        <WithoutResults type="PARTNERS-TABLE" />
      )}
      {rows.length > 0 && (
        <>
          <Box display={{ base: 'none', lg: 'block' }}>
            <Table
              size="sm"
              tableLayout="fixed"
              w="100%"
              sx={{
                th: {
                  bg: 'beige.500',
                  color: 'green.500',
                  px: 2,
                  py: 3,
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere'
                },
                td: {
                  px: 2,
                  py: 2,
                  fontSize: 'sm',
                  overflowWrap: 'anywhere',
                  verticalAlign: 'middle'
                }
              }}
            >
              <colgroup>
                {['4%', '25%', '13%', '9%', '17%', '14%', '10%', '8%'].map(
                  (width, i) => (
                    <col key={i} style={{ width }} />
                  )
                )}
              </colgroup>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Nombres y apellidos</Th>
                  <Th>RUT</Th>
                  <Th>Código</Th>
                  <Th>Grado y grupo jerárquico</Th>
                  <Th>Estado</Th>
                  <Th title="Creado manualmente">Manual</Th>
                  <Th textAlign="center">Opciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((item, index) => (
                  <Tr key={item._id}>
                    <Td color="gray.500">{index + 1 + page * limit}</Td>
                    <Td>
                      <PartnerName partner={item} />
                    </Td>
                    <Td>{item.rut}</Td>
                    <Td>{item.code || '—'}</Td>
                    <Td>
                      <Hierarchy partner={item} />
                    </Td>
                    <Td>
                      <PartnerState state={item.state} />
                    </Td>
                    <Td>{item.isManualCreated ? 'Sí' : 'No'}</Td>
                    <Td textAlign="center">{options(item)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Box display={{ base: 'block', lg: 'none' }}>
            {rows.map((item) => (
              <Box
                key={item._id}
                borderBottom="1px solid"
                borderColor="beige.500"
                py={3}
              >
                <Flex align="start" gap={3} justify="space-between">
                  <Box minW={0}>
                    <PartnerName partner={item} />
                    <Box mt={1}>
                      <PartnerState state={item.state} />
                    </Box>
                  </Box>
                  {options(item)}
                </Flex>
                <SimpleGrid
                  columns={2}
                  gap={2}
                  mt={2}
                  fontSize="sm"
                  sx={{ '& > div': { minWidth: 0, overflowWrap: 'anywhere' } }}
                >
                  <Box>
                    <Text as="span" color="gray.500">
                      RUT:{' '}
                    </Text>
                    {item.rut}
                  </Box>
                  <Box>
                    <Text as="span" color="gray.500">
                      Código:{' '}
                    </Text>
                    {item.code || '—'}
                  </Box>
                  <Box>
                    <Hierarchy partner={item} />
                  </Box>
                  <Box>
                    <Text as="span" color="gray.500">
                      Creado manualmente:{' '}
                    </Text>
                    {item.isManualCreated ? 'Sí' : 'No'}
                  </Box>
                </SimpleGrid>
              </Box>
            ))}
          </Box>
          <Flex
            justify="space-between"
            align="center"
            gap={2}
            wrap="wrap"
            mt={3}
          >
            <Flex align="center" gap={4} wrap="wrap">
              <Text fontSize="sm" color="gray.600">
                {page * limit + 1}–
                {Math.min((page + 1) * limit, partners.totalRecords)} de{' '}
                {partners.totalRecords?.toLocaleString('es-CL')} socios
              </Text>
              <Flex align="center" gap={2}>
                <Text
                  as="label"
                  htmlFor="partners-page-size"
                  fontSize="sm"
                  color="gray.600"
                >
                  Registros por página
                </Text>
                <Select
                  id="partners-page-size"
                  size="sm"
                  w="80px"
                  borderRadius={6}
                  borderColor="green.500"
                  value={limit}
                  onChange={(event) => {
                    setLimit(Number(event.target.value))
                    setPage(0)
                  }}
                >
                  {[10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </Select>
              </Flex>
            </Flex>
            <Box
              minW={0}
              maxW="100%"
              sx={{
                '& > div': { marginTop: 0 },
                '.pagination': {
                  flexWrap: 'wrap',
                  gap: '4px',
                  padding: '4px 0',
                  justifyContent: 'flex-start'
                },
                '.pagination a': {
                  display: 'block',
                  padding: '5px 8px',
                  fontSize: '0.85rem'
                }
              }}
            >
              <CustomPaginate
                totalRecords={partners.totalRecords}
                page={page}
                setPage={setPage}
                perPage={limit}
              />
            </Box>
          </Flex>
        </>
      )}
      <ResignationModal
        isOpen={isOpen}
        onClose={onClose}
        currentId={currentId}
      />
    </Box>
  )
}
export default TablePartners
